import { useEffect, useState } from 'react';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  AlertCircle,
  ChevronLeft,
  EditIcon,
  ExternalLink,
  Loader2Icon,
  PlusIcon,
  PrinterIcon,
} from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';

import { DialogDeleteRecordConfirmation } from '../../../components/pages/backoffice/dialogs/delete-record.jsx';
import { BackOfficePermissionsWrapper } from '../../../components/pages/backoffice/permissions-wrapper.jsx';
import CustomerCoupons from '../../../components/pages/backoffice/printables/customer-coupons-receipt.jsx';
import DecorativeInput from '../../../components/decorative-input.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../components/table/data-table-column-header.jsx';
import { DataTableFooter } from '../../../components/table/data-table-footer.jsx';
import {
  DataTableFacetedFilter,
  DataTableToolbar,
} from '../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Badge } from '../../../components/ui/badge.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Checkbox } from '../../../components/ui/checkbox.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import { DebouncedInput } from '../../../components/ui/input-debounced.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { print } from '../../../helpers/print.js';
import { useTable } from '../../../hooks/use-table.jsx';
import {
  INVOICES_STATES,
  TABLE_FILTER_LIKE,
} from '../../../utils/constants.js';
import { filterIgnoreAccents } from '../../../utils/filters.js';

const COUPONS_TABLE_COLUMNS = [
  {
    title: 'Código',
    accessorKey: 'code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Código" />
    ),
    cell: ({ row }) => <div>{row.getValue('coupon')?.ROWID}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Campanha',
    accessorKey: 'campaign',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Campanha" />
    ),
    cell: ({ row }) => {
      return (
        <Link to={`/backoffice/campaigns/${row.getValue('campaign').ROWID}`}>
          <div className="underline">{row.getValue('campaign').name}</div>
        </Link>
      );
    },
  },
  {
    title: 'Fatura',
    accessorKey: 'invoice',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fatura" />
    ),
    cell: ({ row }) => {
      return (
        <Link to={`/backoffice/invoices/${row.getValue('invoice').ROWID}`}>
          <div className="underline">{row.getValue('invoice').code}</div>
        </Link>
      );
    },
  },
  {
    title: 'Fatura (€)',
    accessorKey: 'invoiceAmount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fatura (€)" />
    ),
    cell: ({ row }) => {
      return <div>{row.getValue('invoice').amount}</div>;
    },
  },
  {
    title: 'Loja',
    accessorKey: 'shop',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Loja" />
    ),
    cell: ({ row }) => {
      return (
        <Link to={`/backoffice/admin/shops/${row.getValue('shop').ROWID}`}>
          <div className="underline">{row.getValue('shop').name}</div>
        </Link>
      );
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'coupon',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => (
      <div className="w-[80px]">
        {row.getValue('coupon')?.CREATEDTIME?.slice(0, -4)}
      </div>
    ),
    enableSorting: true,
    enableHiding: true,
  },
];

const INVOICES_TABLE_COLUMNS = [
  {
    id: 'select',
    header: ({ table }) => (
      <div className="pr-2">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Selecionar tudo"
          className="translate-y-[2px]"
        />
      </div>
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Selecionar linha"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Código',
    accessorKey: 'code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Código" />
    ),
    cell: ({ row }) => {
      return (
        <Link
          to={
            row.original.status === 'Pendente'
              ? `/backoffice/invoices/${row.original.id}/edit`
              : `/backoffice/invoices/${row.original.id}`
          }
        >
          <div className="underline">{row.getValue('code')}</div>
        </Link>
      );
    },
    enableSorting: false,
    enableHiding: false,
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    title: 'Estado',
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      return row.getValue('status') === 'Aprovada' ? (
        <Badge className="min-w-24 justify-center" variant="success">
          {row.getValue('status')}
        </Badge>
      ) : row.getValue('status') === 'Rejeitada' ? (
        <Badge className="min-w-24 justify-center" variant="destructive">
          {row.getValue('status')}
        </Badge>
      ) : (
        <Badge
          className="bg-amber-500 hover:bg-amber-500 dark:text-stone-800"
          variant="secondary"
        >
          {row.getValue('status')}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    title: 'Montante',
    accessorKey: 'amount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Montante" />
    ),
    cell: ({ row }) => {
      return <div>{row.getValue('amount')} €</div>;
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      return (
        <div>{format(row.getValue('createdTime'), 'dd-MM-yyyy HH:mm:ss')}</div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'campaigns.name',
    title: 'Campanha',
    accessorKey: 'campaign',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Campanha" />
    ),
    cell: ({ row }) => {
      if (row.original.campaignId) {
        return (
          <Link to={`/backoffice/campaigns/${row.original.campaignId}`}>
            <div className="underline">{row.original.campaign}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    id: 'shops.name',
    title: 'Loja',
    accessorKey: 'shop',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Loja" />
    ),
    cell: ({ row }) => {
      if (row.original.shopId) {
        return (
          <Link to={`/backoffice/admin/shops/${row.original.shopId}`}>
            <div className="underline">{row.original.shop}</div>
          </Link>
        );
      }

      return <div>---</div>;
    },
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    id: 'hasProof',
    title: 'Comprovativo',
    accessorKey: 'hasProof',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Comprovativo" />
    ),
    cell: ({ row }) => {
      return row.getValue('hasProof') ? <Badge>Comprovativo</Badge> : null;
    },
  },
  {
    title: 'staff',
    accessorKey: 'staff',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Alterado Por" />
    ),
    cell: ({ row }) => {
      return (
        <Link to={`/backoffice/admin/staff/${row.original.staffId}`}>
          <div className="underline">{row.getValue('staff')}</div>
        </Link>
      );
    },
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
  },
  {
    title: 'reason',
    accessorKey: 'reason',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Motivo" />
    ),
    cell: ({ row }) => <div>{row.getValue('reason')}</div>,
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      return (
        <div className="flex flex-row gap-2">
          <Link
            target="_blank"
            rel="noopener noreferrer"
            to={
              row.original.status === 'Aprovada'
                ? `/backoffice/invoices/${row.original.id}`
                : `/backoffice/invoices/${row.original.id}/edit`
            }
          >
            <Button variant="outline" className="h-auto p-2">
              <ExternalLink className="size-4" />
            </Button>
          </Link>
        </div>
      );
    },
  },
];

export function CustomersShow() {
  const { user, isAdmin } = useSnapshot(authState);
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();

  const [printCoupons, setPrintCoupons] = useState(false);

  const queryCustomer = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const { data: customer } = await msCatalystApi.get(
        `/customers/${customerId}`,
      );

      return customer.data;
    },
  });

  const mutationDeleteCustomer = useMutation({
    mutationFn: async () => {
      await msCatalystApi.delete(`/customers/${customerId}`);
    },
    onError: (err) => {
      toast.error('Erro ao apagar cliente');
    },
  });

  const mutationCustomerCoupons = useMutation({
    mutationFn: async () => {
      const { data: coupons } = await msCatalystApi.get(
        `coupons/all?invoices.customerId=${customerId}&order_by=CREATEDTIME,desc&campaigns.ongoing=true`,
      );

      return coupons.data;
    },
    onSuccess: (data) => {
      const campaignName = data[0].campaigns.name;
      const campaignLegalPhrase = data[0].campaigns.legalPhrase;

      print({
        component: (
          <CustomerCoupons
            user={user}
            customer={queryCustomer.data}
            coupons={data.map((item) => item.coupons)}
            campaign={{ name: campaignName, legalPhrase: campaignLegalPhrase }}
          />
        ),
      });
    },
  });

  const {
    table: tableCoupons,
    query: queryCoupons,
    // filters: filtersCoupons,
  } = useTable(
    COUPONS_TABLE_COLUMNS,
    {
      queryKey: ['customer_coupons'],
      queryFn: async () => {
        const { data: coupons } = await msCatalystApi.get(
          `coupons/paginate?invoices.customerId=${customerId}&campaigns.ongoing=true&order_by=CREATEDTIME,desc`,
        );
        return coupons.data;
      },
      placeholderData: keepPreviousData,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => ({
            coupon: item.coupons,
            invoice: item.invoices,
            campaign: item.campaigns,
            shop: item.shops,
          })),
        };
      },
      enabled: !!queryCustomer.data?.ROWID,
    },
    {
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
      searchQs: false,
    },
  );

  const {
    table: tableInvoices,
    query: queryInvoices,
    filters: filtersInvoices,
  } = useTable(
    INVOICES_TABLE_COLUMNS,
    {
      queryKey: ['customer_invoices'],
      queryFn: async () => {
        const { data: invoices } = await msCatalystApi.get(
          `invoices/paginate?customerId=${customerId}&order_by=CREATEDTIME,desc&campaigns.ongoing=true&${filtersInvoices}`,
        );

        return invoices.data;
      },
      placeholderData: keepPreviousData,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => ({
            id: item.invoices.ROWID,
            code: item.invoices.code,
            amount: item.invoices.amount,
            status: item.invoices.status,
            reason: item.invoices.reason,
            customerId: item.customers.ROWID,
            campaigns: item.campaigns,
            campaign: item.campaigns.name,
            campaignId: item.campaigns.ROWID,
            shop: item.shops.name,
            shopId: item.shops.ROWID,
            hasProof: !item.invoices.proofFilename ? false : true,
            staffId: item.staff.ROWID,
            staff: `${item.staff.firstName || ''} ${item.staff.lastName || ''}`,
            createdTime: item.invoices.CREATEDTIME,
          })),
        };
      },
      enabled: !!queryCustomer.data?.ROWID,
    },
    {
      selectColumnFilter: 'code',
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
      searchQs: false,
    },
  );

  useEffect(() => {
    const fetchDataAndPrint = async () => {
      const invoices = searchParams.get('invoices');
      const campaignName = searchParams.get('campaign_name');

      if (invoices && campaignName) {
        setPrintCoupons(true);

        try {
          const invoiceIds = invoices.split(',');

          const { data: couponsData } = await msCatalystApi.get(
            `coupons?invoices.ROWID=in:${invoiceIds}`,
          );

          print({
            component: (
              <CustomerCoupons
                user={user}
                customer={queryCustomer.data}
                coupons={couponsData?.data.map((item) => item.coupons)}
                campaign={campaignName}
              />
            ),
          });
        } catch (error) {
          console.error('Failed to fetch coupons data:', error);
        } finally {
          setPrintCoupons(false);
        }
      }
    };

    fetchDataAndPrint();
  }, []);

  return queryCustomer.isLoading ? (
    <div className="flex h-[70dvh] items-center justify-center">
      <Loading />
    </div>
  ) : queryCustomer.isError ? (
    <Error />
  ) : !queryCustomer.data ? (
    <div className="flex flex-col items-center">
      <AlertCircle className="size-20" />
      <h1 className="py-6 text-xl">O cliente que procura não existe...</h1>
      <Button variant="link" asChild>
        <ChevronLeft className="mr-2 size-4" />
        <Link href="/backoffice/customers">Voltar à página de clientes</Link>
      </Button>
    </div>
  ) : (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <div>
          <Heading className="pb-2">Cliente</Heading>
          <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
            {queryCustomer.data.fullName}
          </h2>
        </div>

        <div className="flex flex-row flex-wrap justify-end gap-4">
          {isAdmin ? (
            <DialogDeleteRecordConfirmation
              recordName={`${queryCustomer.data.firstName} ${queryCustomer.data.lastName}`}
              redirect="/backoffice/customers"
              action={async () => await mutationDeleteCustomer.mutateAsync()}
            />
          ) : null}

          <BackOfficePermissionsWrapper>
            <Link to="edit">
              <Button>
                <EditIcon />
                Editar
              </Button>
            </Link>
          </BackOfficePermissionsWrapper>

          <BackOfficePermissionsWrapper>
            <Link
              to={`/backoffice/invoices/create?customer.cc=${queryCustomer.data.cc}`}
            >
              <Button>
                <PlusIcon />
                Inserir Fatura
              </Button>
            </Link>
          </BackOfficePermissionsWrapper>
        </div>
      </div>

      <div className="flex flex-col space-y-10">
        <div className="flex flex-col gap-5">
          <FormCard>
            <DecorativeInput
              value={queryCustomer.data.firstName}
              label="Nome"
            />

            <DecorativeInput
              value={queryCustomer.data.lastName}
              label="Apelido"
            />

            <DecorativeInput
              value={queryCustomer.data.cc}
              label="Nº Identificação"
            />

            <DecorativeInput
              value={format(queryCustomer.data.birthDate, 'dd-MM-yyyy')}
              label="Data de Nascimento"
            />

            <DecorativeInput value={queryCustomer.data.email} label="Email" />

            <DecorativeInput
              value={queryCustomer.data.phone}
              label="Telemóvel"
            />

            <DecorativeInput
              value={queryCustomer.data.location}
              label="Localidade"
            />

            <DecorativeInput
              value={queryCustomer.data.address}
              label="Morada"
            />

            <DecorativeInput
              value={queryCustomer.data.district}
              label="Distrito"
            />

            <DecorativeInput
              value={queryCustomer.data.zipCode}
              label="Código de Postal"
            />

            <div className="col-span-1 flex flex-row items-center space-x-2 lg:col-span-2">
              <Checkbox
                className="mr-1 size-5"
                disabled={true}
                checked={queryCustomer.data.consentTermsOfParticipation}
              />
              <p className="ml-4 space-y-0 text-sm font-medium leading-none">
                Consentimento de Condições de Participação
              </p>
            </div>

            <div className="col-span-1 flex flex-row items-center space-x-2 lg:col-span-2">
              <Checkbox
                className="mr-1 size-5"
                disabled={true}
                checked={queryCustomer.data.consentDataSharing}
              />
              <p className="ml-4 space-y-0 text-sm font-medium leading-none">
                Consentimento de Comunicações de Marketing
              </p>
            </div>

            <div className="col-span-1 flex flex-row items-center space-x-2 lg:col-span-2">
              <Checkbox
                className="mr-1 size-5"
                disabled={true}
                checked={queryCustomer.data.consentDataSharingLivliFamily}
              />
              <p className="ml-4 space-y-0 text-sm font-medium leading-none">
                Consentimento de Adesão ao Programa de Vantagens Livli Family
              </p>
            </div>
          </FormCard>
        </div>

        {/* Faturas da Campanha Ativa */}
        <div className="flex flex-col gap-5">
          <h2 className="text-xl font-medium">
            Faturas <span className="text-base">(Campanha Ativa)</span>
          </h2>

          {queryInvoices.isLoading || queryInvoices.isPending ? (
            <div className="flex items-center justify-center">
              <Loading />
            </div>
          ) : queryInvoices.isError ? (
            <Error />
          ) : (
            <div className="space-y-4">
              {/* TABLE HEADER */}
              <DataTableToolbar table={tableInvoices} showViewOptions={false}>
                <DebouncedInput
                  placeholder={`Pesquisar código`}
                  value={
                    tableInvoices.getColumn('code')?.getFilterValue() ?? ''
                  }
                  onChange={(val) =>
                    tableInvoices.getColumn('code')?.setFilterValue(val)
                  }
                  className="h-10 md:w-[255px]"
                />

                {tableInvoices.getColumn('hasProof') && (
                  <DataTableFacetedFilter
                    column={tableInvoices.getColumn('hasProof')}
                    title="Comprovativo"
                    search={false}
                    options={[
                      { label: 'Com Comprovativo', value: 'Receipt' },
                      {
                        label: 'Sem Comprovativo',
                        value: 'None',
                      },
                    ]}
                  />
                )}

                {tableInvoices.getColumn('status') && (
                  <DataTableFacetedFilter
                    column={tableInvoices.getColumn('status')}
                    title="Estado"
                    options={[
                      { label: 'Pendente', value: INVOICES_STATES.PENDING },
                      { label: 'Aprovada', value: INVOICES_STATES.APPROVED },
                      { label: 'Rejeitada', value: INVOICES_STATES.REJECTED },
                      { label: 'Anulada', value: INVOICES_STATES.VOIDED },
                    ]}
                  />
                )}
              </DataTableToolbar>

              {/* TABLE SELECTION SECTION */}
              {!!tableInvoices.getSelectedRowModel().rows.length && (
                <div className="flex w-full items-center justify-between rounded-md border border-muted-foreground/35 bg-secondary p-4">
                  <span className="text-sm">
                    {tableInvoices.getSelectedRowModel().rows.length} fatura(s)
                    selecionada(s)
                  </span>

                  <div className="flex items-center gap-4">
                    <Button
                      disabled={printCoupons}
                      onClick={async () => {
                        const campaigns = new Set();
                        for (const item of tableInvoices.getSelectedRowModel()
                          .rows) {
                          campaigns.add(item.original.campaign);
                        }

                        if (campaigns.size > 1) {
                          toast.error(
                            'Não é possível imprimir faturas de campanhas diferentes',
                            {
                              description:
                                'Por favor escolha faturas de uma só campanha',
                            },
                          );
                        } else {
                          try {
                            setPrintCoupons(true);

                            const campaignLegalPhrase =
                              queryInvoices.data.rows[0].campaigns.legalPhrase;
                            const campaignName =
                              tableInvoices.getSelectedRowModel().rows[0]
                                .original.campaign;
                            const invoiceIds = tableInvoices
                              .getSelectedRowModel()
                              .rows.map((item) => {
                                return item.original.id;
                              })
                              .join(',');

                            const { data: couponsData } =
                              await msCatalystApi.get(
                                `coupons/all?invoices.ROWID=in:${invoiceIds}`,
                              );

                            print({
                              component: (
                                <CustomerCoupons
                                  user={user}
                                  customer={queryCustomer.data}
                                  coupons={couponsData?.data.map(
                                    (item) => item.coupons,
                                  )}
                                  campaign={{
                                    name: campaignName,
                                    legalPhrase: campaignLegalPhrase,
                                  }}
                                />
                              ),
                            });
                          } catch (error) {
                            toast.error('Ocorreu um erro ao imprimir cupões', {
                              description:
                                'Por favor tente novamente mais tarde',
                            });
                          } finally {
                            setPrintCoupons(false);
                          }
                        }
                      }}
                    >
                      {printCoupons ? (
                        'Por favor aguarde...'
                      ) : (
                        <>
                          <PrinterIcon className="mr-2 size-4" /> Imprimir
                          Cupões
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* TABLE */}
              <div className="shadow-sm md:rounded-lg md:border md:border-muted-foreground/35 md:bg-muted/40">
                <Table>
                  <TableHeader>
                    {tableInvoices.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return (
                            <TableHead key={header.id} colSpan={header.colSpan}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {queryInvoices.isRefetching ? (
                      <TableRow>
                        <TableCell
                          colSpan={INVOICES_TABLE_COLUMNS.length}
                          className="h-24 text-center"
                        >
                          <Loading />
                        </TableCell>
                      </TableRow>
                    ) : queryInvoices.isRefetchError ? (
                      <TableRow>
                        <TableCell
                          colSpan={INVOICES_TABLE_COLUMNS.length}
                          className="h-24 text-center"
                        >
                          <Error />
                        </TableCell>
                      </TableRow>
                    ) : tableInvoices.getRowModel().rows?.length ? (
                      tableInvoices.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={INVOICES_TABLE_COLUMNS.length}
                          className="h-24 text-center"
                        >
                          {tableInvoices.getState().columnFilters.length
                            ? 'Não foram encontrados resultados.'
                            : 'Este Cliente não possui quaisquer Faturas na Campanha Ativa.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* TABLE FOOTER */}
              <DataTableFooter table={tableInvoices} pageSize={[25, 50]} />
            </div>
          )}
        </div>

        {/* Cupões da Campanha Ativa */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-start justify-start gap-y-6 pt-4 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
            <h2 className="text-xl font-medium">
              Cupões <span className="text-base">(Campanha Ativa)</span>
            </h2>

            {queryCustomer.isFetched &&
              queryCoupons.isFetched &&
              queryCoupons.data.rows?.length && (
                <Button
                  disabled={mutationCustomerCoupons.isPending}
                  onClick={async () => {
                    await mutationCustomerCoupons.mutateAsync();
                  }}
                >
                  {mutationCustomerCoupons.isPending ? (
                    <Loader2Icon className="animate-spin" />
                  ) : (
                    <PrinterIcon />
                  )}
                  Imprimir todos os Cupões
                </Button>
              )}
          </div>

          <div className="shadow-sm md:rounded-lg md:border md:border-muted-foreground/35 md:bg-muted/40">
            <Table>
              <TableHeader>
                {tableCoupons.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {queryCoupons.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={COUPONS_TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryCoupons.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={COUPONS_TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : tableCoupons.getRowModel().rows?.length ? (
                  tableCoupons.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={COUPONS_TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      Este Cliente não possui quaisquer Cupões na Campanha
                      Ativa.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* TABLE FOOTER */}
          <DataTableFooter table={tableCoupons} pageSize={[25, 50]} />
        </div>
      </div>

      <FormFooter>
        {isAdmin ? (
          <DialogDeleteRecordConfirmation
            recordName={`${queryCustomer.data.firstName} ${queryCustomer.data.lastName}`}
            redirect="/backoffice/customers"
            action={async () => await mutationDeleteCustomer.mutateAsync()}
          />
        ) : null}

        <BackOfficePermissionsWrapper>
          <Link to={`/backoffice/customers/${customerId}/edit`}>
            <Button>
              <EditIcon />
              Editar
            </Button>
          </Link>
        </BackOfficePermissionsWrapper>

        <BackOfficePermissionsWrapper>
          <Link
            to={`/backoffice/invoices/create?customer.cc=${queryCustomer.data.cc}`}
          >
            <Button>
              <PlusIcon />
              Inserir Fatura
            </Button>
          </Link>
        </BackOfficePermissionsWrapper>
      </FormFooter>
    </>
  );
}
